import { gql } from '@apollo/client';
import { isEmpty } from 'lodash';

import buildLimitAndOffset from 'api/pagination/buildLimitAndOffset';
import tag from 'api/tags/tag';
import SORTABLE_PIPELINE_FIELDS from './config/SORTABLE_PIPELINE_FIELDS';

export const PIPELINES_QUERY = 'PipelinesList';

export const GET_PIPELINES = gql`
  query ${PIPELINES_QUERY}(
    $limit: Int!
    $offset: Int!
    $clusterIdFilter: pipelines_bool_exp!
    $searchTerm: String!
    $orderBy: [pipelines_order_by!]
    $integrationIdFilter: pipelines_bool_exp!
    $playbookIdFilter: pipelines_bool_exp!
    $tagIdFilter: pipelines_bool_exp!
  ) {
    pipelinesAggregate: pipelines_aggregate(
      where: {
        _and: [
          $clusterIdFilter,
          { deletedAt: { _is_null: true } },
          { _or: [
            { name: { _ilike: $searchTerm } }
            { writekey: { _ilike: $searchTerm } }
          ] },
          $integrationIdFilter,
          $playbookIdFilter,
          $tagIdFilter
        ]
      }
    ) {
      aggregate {
        count
      }
    }
    pipelines(
      limit: $limit
      offset: $offset
      order_by: $orderBy
      where: {
        _and: [
          $clusterIdFilter,
          { deletedAt: { _is_null: true } },
          { _or: [
            { name: { _ilike: $searchTerm } }
            { writekey: { _ilike: $searchTerm } }
          ] },
          $integrationIdFilter,
          $playbookIdFilter,
          $tagIdFilter
        ]
      }
    ) {
      id
      name
      writekey
      cluster {
        id
        name
      }
      pipelineIntegrations: pipeline_integrations {
        id
        integrationId
        pipelineId
        disabled
        integration {
          id
          starterkitId
          name
        }
        consent: pipeline_integration_consent {
          mode
          category: consent_category {
            code
          }
        }
        revision: staged_revision {
          id
          stagedForDelete
          pipelineVars
          playbook {
            revision
            pipelineVars: pipeline_variables {
              id
              name
            }
          }
        }
        pipeline {
          id
          name
        }
      }
      pipelineTags: pipeline_tags(order_by: { created_at: asc }) {
        id
        tag {
          ...tag
        }
      }
    }
  }
  ${tag}
`;

export function buildVariables({
  clusterIds,
  page,
  perPage,
  searchTerm = '',
  integrationIds,
  playbookIds,
  tagIds,
  sort,
}) {
  const clusterIdFilter = !isEmpty(clusterIds) ? { clusterId: { _in: clusterIds } } : {};
  const integrationIdFilter = !isEmpty(integrationIds)
    ? { pipeline_integrations: { integration: { id: { _in: integrationIds } } } }
    : {};
  const playbookIdFilter = !isEmpty(playbookIds)
    ? { pipeline_integrations: { staged_revision: { playbook: { id: { _in: playbookIds } } } } }
    : {};
  const tagIdFilter = !isEmpty(tagIds) ? { pipeline_tags: { tagId: { _in: tagIds } } } : {};

  return {
    ...buildLimitAndOffset({ page, perPage }),
    searchTerm: `%${searchTerm}%`,
    clusterIdFilter,
    integrationIdFilter,
    playbookIdFilter,
    tagIdFilter,
    orderBy: buildOrderBy(sort),
  };
}

function buildOrderBy(sort) {
  switch (sort.field) {
    case SORTABLE_PIPELINE_FIELDS.integrations: {
      return {
        pipeline_integrations_aggregate: { count: sort.direction },
      };
    }
    case SORTABLE_PIPELINE_FIELDS.cluster: {
      return {
        cluster: { name: sort.direction },
      };
    }
    default: {
      return { [sort.field]: sort.direction };
    }
  }
}
